<template>
  <span
    :style="{ 'background-color': baseColourWithOpacity }"
    :class="[
      `aha-option--${variant}`,
      {
        'aha-option--selected': checked,
        'aha-option--disabled': disabled || isWaitingQuizResult,
        'cursor-pointer': (!disabled && !showResult) || isCanSelectOption,
        'aha-option--unselected': !checked && showResult,
        'aha-option--correct': checked && showResult && correct,
        'aha-option--incorrect': checked && showResult && !correct,
        'aha-option--selected-show-result': checked && showResult && !isSubmitted
          && isCanSelectOption,
        'aha-option--disable-pointer': isSubmitted && isCanSelectOption || isWaitingQuizResult,
      }
    ]"
    @click="() => handleInputChange(id)"
  >
    <template v-if="variant === 'image'">
      <div class="relative">
        <div class="aha-option__view-image" @click="openImage($event, image)" v-if="image">
          <img src="@/assets/images/view-image.png" alt="" width="24">
        </div>
        <div class="aha-option__backdrop" />
        <img
          v-if="image"
          :src="image"
          alt="option"
          class="aha-option__image"
          @load="$emit('handle-image-load')"
        >

        <img
          v-else
          src="../../assets/images/image.png"
          alt="option"
          class="aha-option__image"
          @load="$emit('handle-image-load')"
        >

        <AhaCheckbox
          size="lg"
          :checked="checked"
          v-if="multipleSelect"
          class="absolute right-2 bottom-2"
          :disabled="disabled || showResult || isWaitingQuizResult"
        />

        <AhaRadio
          v-else
          size="lg"
          :checked="checked"
          class="absolute right-2 bottom-2"
          :disabled="disabled || showResult || isWaitingQuizResult"
        />

        <span class="aha-option__icon-container">
          <AhaSvg
            v-if="showResult && correct"
            name="correct"
            view-box="0 0 18 18"
            class="aha-option__icon"
            maintain-color
          />

          <AhaSvg
            v-if="showResult && !correct"
            name="incorrect"
            view-box="0 0 18 18"
            maintain-color
            class="aha-option__icon"
          />

          <div
            class="aha-option__user-count animate__animated animate__fadeIn animate__faster"
            :class="{
              'invisible': !showNumberOfSameAnswer,
              'overlay': isOptionUserCountOverlayVisible,
            }"
          >
            <span class="mr-1">
              <AhaFormatter v-if="!showPercent" :value="numberOfSameAnswer" type="number" />

              <AhaFormatter
                v-else
                type="percentage"
                :value="numberOfSameAnswer / numberOfAnswers"
              />
            </span>

            <AhaSvg
              name="user-solid"
              view-box="0 0 16 17"
            />
          </div>
        </span>
      </div>

      <div class="aha-option__label" :style="{ color: textColour }">
        {{ title }}
      </div>
    </template>

    <template v-if="variant === 'text'">
      <span class="items-center d-flex">
        <AhaSvg
          v-if="showResult && correct"
          name="correct"
          view-box="0 0 18 18"
          maintain-color
          class="aha-option__icon"
        />

        <AhaSvg
          v-if="showResult && !correct"
          name="incorrect"
          view-box="0 0 18 18"
          maintain-color
          class="aha-option__icon"
        />

        <template v-if="!showResult">
          <AhaCheckbox
            class="flex-shrink-0"
            has-margin-right
            v-if="multipleSelect"
            :checked="checked"
            :disabled="disabled || isWaitingQuizResult"
          />

          <AhaRadio
            v-else
            has-margin-right
            class="flex-shrink-0"
            :checked="checked"
            :disabled="disabled || isWaitingQuizResult"
          />
        </template>

        <span class="aha-option__label" :style="{ color: textColour }">
          {{ title }}
        </span>
      </span>

      <span
        :style="{ color: textColour }"
        class="aha-option__user-count animate__animated animate__fadeIn animate__faster"
        :class="{
          'invisible': !showNumberOfSameAnswer,
          'overlay': isOptionUserCountOverlayVisible,
        }"
      >
        <span class="mr-1">
          <AhaFormatter v-if="!showPercent" :value="numberOfSameAnswer" type="number" />

          <AhaFormatter
            v-else
            type="percentage"
            :value="numberOfSameAnswer / numberOfAnswers"
          />
        </span>

        <AhaSvg
          name="user-solid"
          view-box="0 0 16 17"
        />
      </span>
    </template>
  </span>
</template>

<script>
// utils
import utils from '@/util'
import PhotoSwipe from 'photoswipe'
import PhotoSwipeUIDefault from 'photoswipe/dist/photoswipe-ui-default'

import AhaCheckbox from '../common/Checkbox.vue'
import AhaRadio from '../common/Radio.vue'

export default {
  components: {
    AhaCheckbox,
    AhaRadio,
  },
  model: {
    props: 'value',
    event: 'input',
  },
  props: {
    image: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    variant: {
      type: String,
      default: 'text',
    },
    id: {
      type: Number,
      default: null,
    },
    value: {
      type: [Number, Array],
      default: null,
    },
    multipleSelect: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showResult: {
      type: Boolean,
      default: false,
    },
    showNumberOfSameAnswer: {
      type: Boolean,
      default: false,
    },
    correct: {
      type: Boolean,
      default: false,
    },
    numberOfSameAnswer: {
      type: Number,
      default: 0,
    },
    numberOfAnswers: {
      type: Number,
      default: null,
    },
    textColour: {
      type: String,
      required: true,
    },
    baseColour: {
      type: String,
      required: true,
    },
    isCanSelectOption: {
      type: Boolean,
      default: false,
    },
    isSubmitted: {
      type: Boolean,
      default: false,
    },
    zoomFullSizeImage: {
      type: Boolean,
      default: false,
    },
    showPercent: {
      type: Boolean,
      default: false,
    },
    isOptionUserCountOverlayVisible: {
      type: Boolean,
      default: false,
    },
    isWaitingQuizResult: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    checked() {
      if (this.value && typeof this.value === 'object') {
        return this.value.includes(this.id)
      }

      return this.value === this.id
    },
    baseColourWithOpacity() {
      return utils.hexToRgba(this.baseColour, 0.5)
    },
  },
  methods: {
    openImage(event, img) {
      event.stopPropagation()
      const pswpElement = document.querySelectorAll('.pswp')[0]

      // build items array
      const items = []
      items.push({
        src: img,
        w: -1,
        h: -1,
        title: '',
      })
      // define options (if needed)
      const options = {
        // optionName: 'option value'
        // for example:
        // disable photoswipe from modifying history, avoid conflict with back to last slide
        history: false,
        index: 0, // start at first slide
      }

      // Initializes and opens PhotoSwipe
      const gallery = new PhotoSwipe(
        pswpElement,
        PhotoSwipeUIDefault,
        items,
        options,
      )

      // $(".pswp__item").addClass("img-none");

      gallery.listen('imageLoadComplete', (index, item) => {
        if (item.w < 1 || item.h < 1) {
          const imgObj = new Image()
          imgObj.src = item.src
          imgObj.onload = function loadingImage() {
            item.w = imgObj.width
            item.h = imgObj.height
            gallery.invalidateCurrItems()
            gallery.updateSize(true)
          }
          // setTimeout(() => {
          //   $(".pswp__item").removeClass("img-none");

          // }, 500)
        }
      })

      gallery.listen('close', () => {
        // $(gallery.container).removeClass('pswp--ready');
      })

      gallery.init()
    },
    handleInputChange(selectedValue) {
      if (this.disabled || this.isWaitingQuizResult) return
      if (!this.isCanSelectOption) {
        if (this.showResult) return
      } else if (this.isSubmitted) return

      const oldValue = this.value

      if (!this.multipleSelect) {
        this.$emit('input', selectedValue)
        return
      }

      if (!oldValue) {
        this.$emit('input', [selectedValue])
        return
      }

      if (oldValue.includes(selectedValue)) {
        let newValue = oldValue.filter((e) => e !== selectedValue)

        if (newValue.length === 0) {
          newValue = null
        }

        this.$emit('input', newValue)
        return
      }

      this.$emit('input', [...oldValue, selectedValue])
    },
  },
}
</script>

<style lang="scss">
.aha-option--text,
.aha-option--image {
  box-shadow: 0 0 0 1px #D3D7E1;
  border-radius: 4px;

  .aha-option__label {
    font-size: 14px;
    color: #282E3E;
  }

  .aha-option__icon {
    background: #FFF;
    border: 1px solid #FFF;
    border-radius: 50%;
  }
}

.aha-option--disabled {
  box-shadow: 0 0 0 2px #D1D1D1;
  background: rgba(181, 181, 181, 0.2);

  .aha-option__label {
    color: #707070;
  }
}

.aha-option--selected {
  box-shadow: 0 0 0 2px #348EF6;
}

.aha-option--image {
  position: relative;
  display: inline-block;
  width: 100%;
  overflow: hidden;

  .aha-option__label {
    padding: 8px;
    text-align: center;
  }

  .aha-option__image {
    display: block;
    width: 100%;
    min-height: 160px;
    object-fit: contain;
  }

  .aha-option__icon-container {
    position: absolute;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .aha-option__icon {
    width: 24px;
    height: 24px;
  }

  .aha-option__user-count {
    color: #FFF;
    font-size: 14px;
    display: flex;
    align-items: center;
    font-weight: bold;
    margin-top: 4px;
    &.overlay {
      background: rgba(0, 0, 0, 0.3);
      padding: 6px 8px;
      border-radius: 3px;
    }
  }
}

.aha-option--text {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  margin-bottom: 12px;
  padding: 10px 12px;

  .aha-option__icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-right: 8px;
  }

  .aha-option__label {
    margin-right: 18px;
    text-align: left;
  }

  .aha-option__user-count {
    font-size: 14px;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    font-weight: bold;
    margin-top: 4px;
  }
}

.aha-option--text:last-child {
  margin-bottom: 0;
}

.aha-option--correct {
  box-shadow: 0 0 0 2px #33C173;
}

.aha-option--incorrect {
  box-shadow: 0 0 0 2px #FF4954;
}

.aha-option--selected-show-result {
  box-shadow: 0 0 0 2px #348EF6;
}

.aha-option--text.aha-option--correct,
.aha-option--image.aha-option--correct .aha-option__backdrop {
  background: rgba(51, 193, 115, 0.5);
}

.aha-option--text.aha-option--incorrect,
.aha-option--image.aha-option--incorrect .aha-option__backdrop {
  background: rgba(255, 73, 84, 0.5);
}

.aha-option--image.aha-option--unselected .aha-option__backdrop {
  background: rgba(0, 0, 0, 0.5);
}

.aha-option__backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.aha-option__view-image {
  position: absolute;
  right: 8px;
  top: 8px;
  cursor: pointer;
  z-index: 10;
}

.aha-option--disable-pointer.cursor-pointer {
  cursor: not-allowed;
}
</style>
